import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {
  CodebookService,
  EntityClassSelectorComponent,
  EntityClassSelectorUsage,
  FileSelectorComponent,
  getObjectTagColor,
  ObjectClassIconComponent,
  RetentionTriggerTypeWidgetComponent,
  RetentionTriggerWidgetMode,
} from '|shared';
import {
  FormAutocompleteComponent,
  FormFieldComponent,
  getTodayMidnight,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczOption,
  IczValidators
} from '@icz/angular-form-elements';
import {injectModalData, injectModalRef} from '@icz/angular-modal';
import {
  ApiCrossReferenceService,
  ApiFileService,
  DisposalConflictRequestDto,
  FileDto,
  ValidRetentionTriggerDataRequestDto
} from '|api/document';
import {
  AlertComponent,
  ButtonComponent,
  FormButtonsComponent,
  LabelComponent,
  LoadingIndicatorService,
  TagComponent,
  WaitingDirective
} from '@icz/angular-essentials';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BehaviorSubject} from 'rxjs';
import {TranslateParser, TranslatePipe, TranslateService} from '@ngx-translate/core';
import {
  DisposalOperationCode,
  EntityClassDto,
  EntityType,
  FileState,
  InternalNotificationKey,
  RetentionTriggerTypeCode,
  StrongCrossReferenceLeadingFileType
} from '|api/commons';
import {FilterOperator, FilterParam} from '@icz/angular-table';
import {ReactiveFormsModule} from '@angular/forms';
import {AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {
  CheckUnsavedFormDialogService,
  enumToOptions,
  IczFormSubmitDirective,
  IFormGroupCheckable,
  interpolateBackendErrorMessage,
  namedDtosToOptionsWithCode
} from '@icz/angular-application-core';
import {ApiFileConfigurationAdministrationService} from '|api/config-server';
import {DisposalScheduleDto} from '|api/codebook';
import {FileToastService, FileToastType} from '|ui-essl-core';

@Component({
  selector: 'icz-strong-cross-reference-create-dialog',
  templateUrl: './strong-cross-reference-create-dialog.component.html',
  styleUrls: ['./strong-cross-reference-create-dialog.component.scss'],
  providers: [
    CheckUnsavedFormDialogService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, IczFormContainerDirective, WaitingDirective, FileSelectorComponent, AlertComponent, EntityClassSelectorComponent, FormAutocompleteComponent, FormFieldComponent, RetentionTriggerTypeWidgetComponent, NgTemplateOutlet, LabelComponent, ObjectClassIconComponent, TagComponent, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, AsyncPipe, TranslatePipe]
})
export class StrongCrossReferenceCreateDialogComponent implements OnInit, IFormGroupCheckable {

  private checkUnsavedService = inject(CheckUnsavedFormDialogService);
  protected loadingService = inject(LoadingIndicatorService);
  protected modalRef = injectModalRef<Nullable<boolean>>();
  protected dialogFileData = injectModalData<FileDto>();
  private destroyRef = inject(DestroyRef);
  private codebookService = inject(CodebookService);
  private translateService = inject(TranslateService);
  private translateParser = inject(TranslateParser);
  private apiFileConfigurationAdministrationService = inject(ApiFileConfigurationAdministrationService);
  protected apiFileService = inject(ApiFileService);
  private apiCrossReferenceService = inject(ApiCrossReferenceService);
  private fileToastService = inject(FileToastService);

  protected readonly getObjectTagColor = getObjectTagColor;

  formGroupsToCheck!: string[];
  filesForStrongCrossReference: FileDto[] = [];
  allFilesForStrongCrossReference: FileDto[] = [];
  closedFilesForStrongCrossReference: FileDto[] = [];
  disposalSchedulesOptions$ = this.codebookService.disposalSchedules().pipe(namedDtosToOptionsWithCode);
  fileSCRLeadingFile: Nullable<StrongCrossReferenceLeadingFileType>;
  isEntityClassConflict = false;
  leadingFile: Nullable<FileDto>;
  isDialogFileLeadingFile = false;
  prepareValidationError = '';
  sameEntityClass = false;
  selectedFile: Nullable<FileDto>;

  isRetentionTriggerTypeWithPeriod = signal(false);

  disposalOperationCodeOptions = enumToOptions('disposalOperationCode', DisposalOperationCode);
  retentionTriggerTypesOptions: IczOption[] = enumToOptions('retentionTriggerTypeCode', RetentionTriggerTypeCode);

  form = new IczFormGroup({
    fileId: new IczFormControl<Nullable<number>>(null, [IczValidators.required()]),
    entityClassId: new IczFormControl<Nullable<number>>(null),
    disposalScheduleId: new IczFormControl<Nullable<number>>(null),
    conflictEntityClassId: new IczFormControl<Nullable<number>>({disabled: true, value: null}),
    yearOfRetentionPeriodStart: new IczFormControl<Nullable<number>>(null),
    triggerEventCheckYear: new IczFormControl<Nullable<number>>(null),
    disposalOperationCode: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    disposalYear: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    externalRetentionTriggerIds: new IczFormControl<Nullable<number[]>>(null),
    retentionTriggerTypeCode: new IczFormControl<Nullable<string>>(null),
  });

  isSCRPrepareLoaded = false;
  retentionTriggerValidationMode = new BehaviorSubject<RetentionTriggerWidgetMode>(RetentionTriggerWidgetMode.VALIDATED);
  disposalScheduleSourceEntitySelectedId: Nullable<number>;
  externalRetentionTriggerIds: number[] = [];

  get fileIdControl() {
    return this.form.get('fileId')! as IczFormControl;
  }

  get retentionTriggerYearCtrl() {
    return this.form.get('yearOfRetentionPeriodStart') as IczFormControl;
  }

  get retentionTriggerTypeCodeCtrl() {
    return this.form.get('retentionTriggerTypeCode') as IczFormControl;
  }

  get retentionTriggerCheckYearCtrl() {
    return this.form.get('triggerEventCheckYear') as IczFormControl;
  }

  get entityClassIdCtrl() {
    return this.form.get('entityClassId')!;
  }

  get disposalScheduleIdCtrl() {
    return this.form.get('disposalScheduleId')!;
  }

  get conflictEntityClassIdCtrl() {
    return this.form.get('conflictEntityClassId')!;
  }

  get isFileSelected() {
    return this.fileIdControl.value;
  }

  conflictEntityClassChange(entityClass: Nullable<EntityClassDto>) {
    this.disposalScheduleSourceEntitySelectedId = entityClass?.disposalScheduleId;
  }

  additionalFilterCriteria: FilterParam[] = [
    {
      fieldName: 'fileState',
      operator: FilterOperator.inSet,
      value: [FileState.OPEN, FileState.SETTLED, FileState.CLOSED],
    },
  ];

  ngOnInit() {
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);
    this.form.get('fileId')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(fileId => {
      this.isSCRPrepareLoaded = false;
      if (fileId === this.dialogFileData.id) {
        this.prepareValidationError = 'Spis není možné propojit pevným křížovým odkazem se sebou samým.';
        this.isSCRPrepareLoaded = true;
      } else {
        this.prepareValidationError = '';
        if (fileId) {
          this.loadingService.doLoading(
            this.apiCrossReferenceService.crossReferencePrepareStrongCrossReference({
              body: {
                fileforCrossReferenceFromId: this.dialogFileData.id,
                fileforCrossReferenceToId: fileId
              }
            }),
            this
          ).subscribe( prepare => {
            if (prepare.validatonErrorCode) {
              this.prepareValidationError = interpolateBackendErrorMessage(this.translateService, {
                errorCode: prepare.validatonErrorCode,
                parameters: [this.dialogFileData.refNumber!, this.selectedFile?.refNumber ?? '']
              });
            } else {
              this.allFilesForStrongCrossReference = prepare.filesForCrossReference;
              this.filesForStrongCrossReference = prepare.filesForCrossReference.filter(f => f.id !== this.dialogFileData.id);
              if (this.allFilesForStrongCrossReference) {
                this.leadingFile = this.allFilesForStrongCrossReference.find(f => f.id === prepare.leadingFileId);
                this.isDialogFileLeadingFile = this.leadingFile!.id === this.dialogFileData.id;
                this.closedFilesForStrongCrossReference = this.filesForStrongCrossReference.filter(file => file.fileState === FileState.CLOSED);
                if (this.leadingFile) {
                  this.form.get('entityClassId')!.setValue(this.leadingFile.entityClassId);
                  this.form.get('disposalScheduleId')!.setValue(this.leadingFile.disposalScheduleId);
                }
                this.validateLeadingFileConflict();
              }
            }
            this.isSCRPrepareLoaded = true;
          });
        }
      }
    });

    this.conflictEntityClassIdCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(entityClassId => {
      if (entityClassId) {
        this.prepareDisposalSchedules(entityClassId);
      }
    });

    this.loadingService.doLoading(
      this.apiFileConfigurationAdministrationService.fileConfigurationAdministrationGetFileConfiguration(),
      this
    ).subscribe(configuration => {
      this.fileSCRLeadingFile = configuration.strongCrossReferenceLeadingFileType;
    });
  }

  onFileSelected(files: FileDto[]) {
    this.selectedFile = files[0];
  }

  validateLeadingFileConflict(): void {
    if (this.leadingFile) {
      if (this.isDialogFileLeadingFile) {
        if (this.closedFilesForStrongCrossReference.length > 0 && this.leadingFile.disposalScheduleId !== this.closedFilesForStrongCrossReference[0].disposalScheduleId) {
          // current dialog file is leading file
          // there is a disposalScheduleId conflict between leading file and closed files in SCR group
          this.setDisposalScheduleConflict();
        } else {
          this.isEntityClassConflict = false;
        }
      } else {
        if (this.dialogFileData.fileState === FileState.CLOSED && this.leadingFile.disposalScheduleId !== this.dialogFileData.disposalScheduleId) {
          // current dialog file is not leading file, but it is a closed file
          // there is a disposalScheduleId conflict between leading file of SCR group and current dialog file
          this.setDisposalScheduleConflict();
        } else {
          this.isEntityClassConflict = false;
        }
      }
      if (!this.isEntityClassConflict) {
        this.sameEntityClass = true;
        for (const f of this.allFilesForStrongCrossReference) {
          this.allFilesForStrongCrossReference.forEach(comparingFile => {
            if (f.id !== comparingFile.id && f.entityClassId !== comparingFile.entityClassId) {
              this.sameEntityClass = false;
            }
          });
          if (!this.sameEntityClass) {
            break;
          }
        }
      }
    }
  }

  setDisposalScheduleConflict() {
    this.isEntityClassConflict = true;
    this.entityClassIdCtrl.clearValidators();
    this.disposalScheduleIdCtrl.clearValidators();
    this.conflictEntityClassIdCtrl.setValue(this.leadingFile!.entityClassId);
  }

  fillRetentionSection(selectedDs: Nullable<DisposalScheduleDto>) {
    if (selectedDs) {
      this.retentionTriggerTypeCodeCtrl.setValue(selectedDs?.retentionTriggerTypeCode);
      this.retentionTriggerCheckYearCtrl.setValue((new Date()).getFullYear() + (selectedDs.defaultTriggerEventCheckPeriod ?? 0));
    }
  }

  prepareDisposalSchedules(entityClassId: number) {
    const disposalDataReq: DisposalConflictRequestDto[] = [];
    const retentionDataReq: ValidRetentionTriggerDataRequestDto[] = [];
    this.closedFilesForStrongCrossReference.forEach(f=> {
      disposalDataReq.push({
        id: f.id,
        entityType: EntityType.FILE,
      });
      retentionDataReq.push({
        entityId: f.id,
        entityClassId
      });
    });
    if (this.dialogFileData.fileState === FileState.CLOSED) {
      disposalDataReq.push({
        id: this.dialogFileData.id,
        entityType: EntityType.FILE
      });
      retentionDataReq.push({
        entityId: this.dialogFileData.id,
        entityClassId
      });
    }

    this.loadingService.startLoading(this);
    this.apiFileService.fileGetValidRetentionTriggerData({
      body: retentionDataReq
    }).subscribe(retentionData => {
      this.retentionTriggerYearCtrl!.setValue(retentionData.globalResult?.defaultYearOfRetentionPeriodStart);
      this.externalRetentionTriggerIds = retentionData.globalResult?.externalRetentionTriggerIds ?? [];

      this.apiCrossReferenceService.crossReferenceGetDisposalData({
        entityClassId,
        body: disposalDataReq
      }).subscribe(result => {
        this.form.get('disposalOperationCode')!.setValue(result.disposalOperationCode);
        this.form.get('disposalYear')!.setValue(result.disposalYear);
        this.loadingService.endLoading(this);
      });
    });
  }

  submit() {
    const fillRetentionYear = this.isEntityClassConflict && this.disposalScheduleSourceEntitySelectedId;
    if (this.prepareValidationError === '') {
      this.loadingService.doLoading(
        this.apiCrossReferenceService.crossReferenceCreateStrongCrossReference({
          body: {
            entityReferringFromId: this.dialogFileData.id,
            entityReferringToId: this.form.get('fileId')!.value!,
            filesForCrossReference: this.allFilesForStrongCrossReference.map(f => f.id),
            entityClassId: this.isEntityClassConflict ? this.conflictEntityClassIdCtrl.value : this.entityClassIdCtrl.value,
            triggerEventCheckYear: fillRetentionYear ? this.form.get('triggerEventCheckYear')!.value : null,
            yearOfRetentionPeriodStart: fillRetentionYear ? this.form.get('yearOfRetentionPeriodStart')!.value : null
          }
        }),
        this
      ).subscribe(_ => {
        this.form.markAsPristine();
        this.modalRef.close(true);
        this.fileToastService.dispatchFileInfoToast(FileToastType.FILE_STRONG_CROSS_REFERENCE_CREATED, {
          [InternalNotificationKey.FILE_ID]: this.dialogFileData.id,
          [InternalNotificationKey.FILE_SUBJECT]: `${this.dialogFileData.refNumber} ${this.dialogFileData.subject}`
        });
      });
    }
  }

  cancel() {
    this.modalRef.close(null);
  }

  noConflictAlertText() {
    if (this.fileSCRLeadingFile) {
      const contextParams = {
        leadingFileType: this.fileSCRLeadingFile === StrongCrossReferenceLeadingFileType.OLDEST ? this.translateService.instant('nejdříve') : this.translateService.instant('nejpozději')
      };
      return this.translateParser.interpolate(
        this.translateService.instant('fe.ui.customFields.help.strongCrossReferenceCreate'),
        contextParams
      )!;
    } else {
      return '';
    }
  }

  protected readonly getTodayMidnight = getTodayMidnight;
  protected readonly FileState = FileState;
  protected readonly EntityClassSelectorUsage = EntityClassSelectorUsage;
}
